import React, { Component } from "react";
import PropTypes from "prop-types";
import { themr } from "react-css-themr";
import classnames from "classnames";
import _ from "lodash";

import {
  Badge,
  Labeled,
  itemList,
  Select,
  ButtonComposed,
  Button,
} from "@lifepay/rc-ui-toolkit";

import ProcessingUserAccesses from "./ProcessingUserAccesses";
import styles from "./ProcessingUser.m.scss";
import { processingUser } from "model/gql/mutations";

const ProcessingUserMutations = itemList.MutationFactory([
  processingUser.role.add,
  processingUser.role.remove,
  processingUser.remove,
  processingUser.block,
  processingUser.unblock,
]);

@themr("@lifepay/User", styles)
export default class ProcessingUser extends Component {
  static displayName = "ProcessingUser";

  static propTypes = {
    theme: PropTypes.object,
    item: PropTypes.object,
    currentUserRoles: PropTypes.object,
    extendedView: PropTypes.bool,
    updateQuery: PropTypes.func,
    refetchUsers: PropTypes.func,
    roles: PropTypes.array,
    rolesDataState: PropTypes.string,
  };

  static defaultProps = {
    extendedView: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      newRoles: null,
      submitButtonState: "normal",
      deleteButtonState: "normal",
      isBlockedState: null,
    };
  }

  handleChange = ({
    allRoles,
    userRoles,
    desiredRoles,
    addUserRole,
    removeUserRole,
  }) => {
    this.setState({ newRoles: desiredRoles });
  };

  submitChanges = ({
    username,
    userRoles,
    addUserRole,
    removeUserRole,
    blockUser,
    unblockUser,
  }) => {
    const { newRoles, isBlockedState } = this.state;
    const { item, updateQuery, roles } = this.props;
    const mutations = [];

    if (isBlockedState !== null) {
      if (isBlockedState) {
        mutations.push(
          blockUser({
            variables: {
              username,
            },
          })
        );
      } else {
        mutations.push(
          unblockUser({
            variables: {
              username,
            },
          })
        );
      }
    }

    if (newRoles) {
      newRoles.forEach((role) => {
        if (!userRoles.includes(role)) {
          // addRole
          mutations.push(
            addUserRole({
              variables: {
                username,
                role,
              },
            })
          );
        }
      });
      userRoles.forEach((role) => {
        if (!newRoles.includes(role)) {
          // removeRole
          mutations.push(
            removeUserRole({
              variables: {
                username,
                role,
              },
            })
          );
        }
      });
    }
    if (mutations.length) {
      this.setState({ submitButtonState: "loading" });
    }

    Promise.all(mutations)
      .then(() => {
        updateQuery((prevData) => {
          const newUsers = prevData.processingUsers.slice();
          const index = _.findIndex(newUsers, {
            id: item.id,
          });
          const oldUser = newUsers[index];
          if (newRoles) {
            const finalRoles = [];
            newRoles.forEach((role) => {
              const index = _.findIndex(roles, {
                roleName: role,
              });
              finalRoles.push(roles[index]);
            });
            newUsers.splice(index, 1, {
              ...oldUser,
              roles: finalRoles,
            });
          }
          if (isBlockedState !== null) {
            newUsers.splice(index, 1, {
              ...oldUser,
              isBlocked: isBlockedState,
            });
          }
          return {
            ...prevData,
            processingUsers: newUsers,
          };
        });
        this.setState({
          isBlockedState: null,
          newRoles: null,
          submitButtonState: "normal",
        });
      })
      .catch(() => {
        this.setState({
          isBlockedState: null,
          newRoles: null,
          submitButtonState: "error",
        });
      });
  };

  clearChanges = () => {
    this.setState({
      isBlockedState: null,
      newRoles: null,
      submitButtonState: "normal",
    });
  };

  render() {
    const {
      theme,
      item,
      updateQuery,
      roles,
      refetchUsers,
      rolesDataState,
    } = this.props;
    const {
      newRoles,
      submitButtonState,
      deleteButtonState,
      isBlockedState,
    } = this.state;

    const userRoles = item.roles.map((role) => role.roleName);
    const isBlockedCurrent =
      isBlockedState !== null ? isBlockedState : item.isBlocked;

    return (
      <ProcessingUserMutations>
        {({
          removeProcessingUser,
          blockProcessingUser,
          unblockProcessingUser,
          addProcessingUserRole,
          removeProcessingUserRole,
        }) => (
          <div className={classnames(theme.wrapper)}>
            <div className={classnames(theme.summary)}>
              <Labeled
                label={"Имя пользователя"}
                theme={{ label: theme.label }}
              >
                {item.id}
              </Labeled>
              <Labeled label={"Статус"}>
                <Badge type={!isBlockedCurrent ? "green" : "red"}>
                  {!isBlockedCurrent ? "Активен" : `Заблокирован`}
                </Badge>
              </Labeled>
              <Labeled label={"Комментарий"} theme={{ label: theme.label }}>
                {item.comment}
              </Labeled>
              <Labeled label={"Роли"} theme={{ label: theme.label }}>
                {rolesDataState === itemList.CONSTANTS.DATA_STATE.LOADED ||
                rolesDataState ===
                  itemList.CONSTANTS.DATA_STATE.LOADED_EMPTY ? (
                  <Select
                    placeholder={""}
                    name={"roles"}
                    id={"roles"}
                    value={newRoles || item.roles.map((role) => role.roleName)}
                    options={roles.map((role) => ({
                      value: role.roleName,
                      label: role.roleName,
                    }))}
                    onChange={({ value }) => {
                      this.handleChange({
                        userRoles,
                        desiredRoles: value,
                        addUserRole: addProcessingUserRole.mutate,
                        removeUserRole: removeProcessingUserRole.mutate,
                      });
                    }}
                    multiple
                    hideChooseAll
                    label={false}
                  />
                ) : (
                  <div>
                    {item.roles.map((role) => role.roleName).join(" , ")}
                  </div>
                )}
              </Labeled>
              <div className={classnames(theme.controls)}>
                {(newRoles || isBlockedState !== null) && (
                  <React.Fragment>
                    <ButtonComposed
                      state={submitButtonState}
                      disabled={submitButtonState !== "normal"}
                      type={"main"}
                      size={"small"}
                      theme={{
                        wrapper: theme.button,
                      }}
                      onClick={() =>
                        this.submitChanges({
                          username: item.id,
                          userRoles,
                          addUserRole: addProcessingUserRole.mutate,
                          removeUserRole: removeProcessingUserRole.mutate,
                          blockUser: blockProcessingUser.mutate,
                          unblockUser: unblockProcessingUser.mutate,
                        })
                      }
                    >
                      {"Подтвердить изменения"}
                    </ButtonComposed>
                    <Button
                      type={"alt"}
                      size={"small"}
                      theme={{
                        wrapper: theme.button,
                      }}
                      onClick={this.clearChanges}
                    >
                      {"Отмена"}
                    </Button>
                  </React.Fragment>
                )}
                {isBlockedCurrent ? (
                  <Button
                    type={"main"}
                    size={"small"}
                    theme={{
                      wrapper: theme.button,
                    }}
                    onClick={() => {
                      if (isBlockedState !== null) {
                        this.setState({ isBlockedState: null });
                      } else {
                        this.setState({
                          isBlockedState: false,
                        });
                      }
                    }}
                  >
                    {"Разблокировать"}
                  </Button>
                ) : (
                  <Button
                    type={"negative"}
                    size={"small"}
                    theme={{
                      wrapper: theme.button,
                    }}
                    onClick={() => {
                      if (isBlockedState !== null) {
                        this.setState({ isBlockedState: null });
                      } else {
                        this.setState({ isBlockedState: true });
                      }
                    }}
                  >
                    {"Заблокировать"}
                  </Button>
                )}
                <ButtonComposed
                  state={deleteButtonState}
                  disabled={deleteButtonState !== "normal"}
                  type={"negative"}
                  size={"small"}
                  theme={{
                    wrapper: theme.button,
                  }}
                  onClick={() => {
                    this.setState({
                      deleteButtonState: "loading",
                    });
                    removeProcessingUser
                      .mutate({
                        variables: {
                          username: item.id,
                        },
                        update: (
                          cache,
                          {
                            data: {
                              deleteProcessingUser: { success },
                            },
                          }
                        ) => {
                          if (success) {
                            updateQuery((prevUsers) => {
                              const newUsers = prevUsers.processingUsers.slice();
                              const index = _.findIndex(newUsers, {
                                id: item.id,
                              });
                              newUsers.splice(index, 1);
                              return {
                                ...prevUsers,
                                processingUsers: newUsers,
                              };
                            });
                          } else {
                            this.setState({
                              deleteButtonState: "error",
                            });
                          }
                        },
                      })
                      .catch(() => {
                        this.setState({
                          deleteButtonState: "error",
                        });
                      });
                  }}
                >
                  {"Удалить"}
                </ButtonComposed>
              </div>
            </div>
            <ProcessingUserAccesses
              roles={item.roles}
              accesses={item.accesses}
              username={item.id}
              refetchUsers={refetchUsers}
            />
          </div>
        )}
      </ProcessingUserMutations>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/list-items/ProcessingUser/ProcessingUser.js